// Time zone and operating hours logic below is kept in sync with the mobile app.

/**
 * Parses a timezone string (e.g. "UTC+00:00", "UTC+09:00", "UTC-06:00", "+01:00", "GMT-5", "Africa/Lagos")
 * into offset minutes relative to UTC (e.g., +540, -360, 0).
 * @param {string} tzString
 * @return {number|null} Offset in minutes from UTC, or null if invalid/unspecified
 */
const parseTimeZoneOffset = (tzString) => {
  if (!tzString || typeof tzString !== "string") return null;

  const str = tzString.trim();

  // Match offset strings like "UTC+09:00", "UTC-06:00", "GMT+1", "+05:30", "-0400", "+9", etc.
  const offsetMatch = str.match(
    /(?:UTC|GMT)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?/i
  );
  if (offsetMatch) {
    const sign = offsetMatch[1] === "-" ? -1 : 1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = offsetMatch[3] ? parseInt(offsetMatch[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  }

  // Fallback for IANA timezone names like "Africa/Lagos", "America/New_York"
  try {
    const now = new Date();
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: str,
      timeZoneName: "shortOffset",
    }).format(now);
    const ianaMatch = formatted.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/i);
    if (ianaMatch) {
      const sign = ianaMatch[1].startsWith("-") ? -1 : 1;
      const hours = Math.abs(parseInt(ianaMatch[1], 10));
      const minutes = ianaMatch[2] ? parseInt(ianaMatch[2], 10) : 0;
      return sign * (hours * 60 + minutes);
    }
  } catch (e) {
    // Ignore invalid IANA timezone string
  }

  return null;
};

/**
 * Calculates current or given date/time attributes adjusted for a specific target timezone string.
 * Compares device local time against the target timezone offset.
 * @param {Date|string|number} [date=new Date()]
 * @param {string} [tzString=null] - e.g. "UTC+09:00", "UTC-06:00"
 * @return {Object} { dayIndex: 0-6 (0=Sunday), hours: 0-23, minutes: 0-59, timeVal: HHMM (e.g. 930), dateObj: Date }
 */
const getDateTimeInTimeZone = (date = new Date(), tzString = null) => {
  const currentDate = date instanceof Date ? date : new Date(date);
  const targetOffsetMinutes = parseTimeZoneOffset(tzString);

  if (targetOffsetMinutes === null) {
    // Default to device local time
    const dayIndex = currentDate.getDay();
    const hours = currentDate.getHours();
    const minutes = currentDate.getMinutes();
    return {
      dayIndex,
      hours,
      minutes,
      timeVal: hours * 100 + minutes,
      dateObj: currentDate,
    };
  }

  // Convert device time -> UTC -> target timezone time
  const utcMillis = currentDate.getTime();
  const targetMillis = utcMillis + targetOffsetMinutes * 60000;
  const targetDate = new Date(targetMillis);

  const dayIndex = targetDate.getUTCDay();
  const hours = targetDate.getUTCHours();
  const minutes = targetDate.getUTCMinutes();

  return {
    dayIndex,
    hours,
    minutes,
    timeVal: hours * 100 + minutes,
    dateObj: targetDate,
  };
};

/**
 * Checks if a restaurant or store is currently open based on operating hours and time zone.
 * @param {Object|string} timeInput - Operating time object/string, or restaurant/store entity object
 * @param {string|Object} [timeZoneInput=null] - Time zone string e.g. "UTC+09:00", or entity object
 * @return {boolean}
 */
const isRestaurantTimeOpen = (timeInput, timeZoneInput = null) => {
  let operatingTime = timeInput;
  let timeZone = timeZoneInput;

  if (!operatingTime) return false;

  // Extract from entity object if passed as first parameter
  if (
    typeof operatingTime === "object" &&
    !operatingTime.monday &&
    !operatingTime.tuesday &&
    !operatingTime.wednesday &&
    !operatingTime.thursday &&
    !operatingTime.friday &&
    !operatingTime.saturday &&
    !operatingTime.sunday
  ) {
    const extractedOpTime =
      operatingTime.restaurant_operating_time ||
      operatingTime.store_operating_time ||
      operatingTime.operating_time;
    const extractedTz =
      operatingTime.restaurant_time_zone ||
      operatingTime.store_time_zone ||
      operatingTime.time_zone;

    if (extractedOpTime) {
      operatingTime = extractedOpTime;
    }
    if (!timeZone && extractedTz) {
      timeZone = extractedTz;
    }
  }

  // Extract timezone if object passed as second parameter
  if (typeof timeZone === "object" && timeZone !== null) {
    timeZone =
      timeZone.restaurant_time_zone ||
      timeZone.store_time_zone ||
      timeZone.time_zone ||
      null;
  }

  // Parse operatingTime if JSON string
  if (typeof operatingTime === "string") {
    try {
      operatingTime = JSON.parse(operatingTime);
    } catch (e) {
      return false;
    }
  }

  if (!operatingTime || typeof operatingTime !== "object") return false;

  // Extract timezone from operatingTime object if present
  if (!timeZone) {
    timeZone =
      operatingTime.restaurant_time_zone ||
      operatingTime.store_time_zone ||
      operatingTime.time_zone ||
      null;
  }

  // Calculate target date & time considering the timezone offset
  const targetTime = getDateTimeInTimeZone(new Date(), timeZone);
  const weekdays = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const currentDayName = weekdays[targetTime.dayIndex];
  const dayHours = operatingTime[currentDayName];

  if (dayHours && dayHours.open && dayHours.close) {
    const convertTimeToVal = (hStr) => {
      if (!hStr || typeof hStr !== "string") return 0;
      const parts = hStr.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h * 100 + m;
    };

    const openVal = convertTimeToVal(dayHours.open);
    const closeVal = convertTimeToVal(dayHours.close);

    if (closeVal >= openVal) {
      return targetTime.timeVal >= openVal && targetTime.timeVal <= closeVal;
    } else {
      // Overnight hours (e.g., open 18:00 to 02:00 next day)
      return targetTime.timeVal >= openVal || targetTime.timeVal <= closeVal;
    }
  }

  return false;
};

/**
 * @param {string} operating_status - e.g. restaurant?.restaurant_operating_status
 * @param {string} operating_settings - e.g. settings?.restaurant_operating_status (global app setting)
 * @param {object|null} operating_hours - e.g. restaurant?.restaurant_operating_time
 * @param {string} outlet_time_zone - e.g. "UTC+01:00"
 */
export function isOutletOpen(
  operating_status,
  operating_settings,
  operating_hours,
  outlet_time_zone = "UTC+00:00"
) {
  if (operating_status === "open" && operating_settings === "available") {
    return (
      operating_hours === null ||
      operating_hours === undefined ||
      isRestaurantTimeOpen(operating_hours, outlet_time_zone)
    );
  }

  return false;
}
